import { Component, Element, Event, EventEmitter, Listen, Prop, State, h } from '@stencil/core';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

@Component({
  tag: 'biel-message',
  styleUrl: 'biel-message.css',
  shadow: true,
})
export class BielMessage {
  @Prop() messageId?: string;
  @Prop() sender: 'user' | 'ai';
  @Prop() text: string;
  @Prop() sources?: { title: string; url: string }[];
  @Prop() feedback?: 0 | 1;
  @Prop() hideFeedback: boolean;
  @Prop() bielId: string;
  @Prop() isPartial: boolean;
  @Prop() partialId: number;
  @Prop() sourcesText: string = 'Sources';

  @State() content: string;
  @State() rawContent: string = '';
  @State() feed: 0 | 1;
  @State() src: { title: string; url: string }[];

  @Element() el: HTMLElement;

  @Event() scrollToBottom: EventEmitter<{ bielId: string }>;

  @Listen('addMessageText', { target: 'body' })
  handleTextUpdate(event: CustomEvent<{ text: string, id: number }>) {
    if (this.isPartial && this.partialId === event.detail.id) {
      this.processMarkdown(event.detail.text);
    }
  }
  @Listen('partialMessageComplete', { target: 'body' })
  handlePartialMessageComplete(event: CustomEvent<{ partialId: number, bielId: string, msg: { text: string, sender: 'user' | 'ai', messageId?: string, feedback?: 0 | 1, sources?: { title: string, url: string }[], isPartial: boolean, partialId?: number } }>) {
    if (this.isPartial && this.partialId === event.detail.partialId) {
      this.feed = event.detail.msg.feedback;
      this.src = event.detail.msg.sources;
    }
  }

  componentWillLoad() {
    this.processMarkdown(this.text);
  }

  componentDidLoad() {
    if (!this.isPartial) {
      const visibleDiv = this.el.shadowRoot.querySelector('#biel-message') as HTMLElement;
      visibleDiv.innerHTML = this.content;
      this.feed = this.feedback;
      this.src = this.sources;
    }
    if (this.sender === 'user') {
      this.scrollToBottom.emit({ bielId: this.bielId });
    }
  }

  processMarkdown(fragment: string) {
    this.rawContent += fragment;
    const markedContent = marked.parse(this.rawContent, { breaks: true }) as string;
    const html = DOMPurify.sanitize(markedContent);
    const htmlSnippet = html
      .replace(/<pre><code/g, `<code-snippet`)
      .replace(/<\/code><\/pre>/g, "</code-snippet>")
      .replace(/<code-snippet([^>]*)>\s*([\s\S]*?)\s*<\/code-snippet>/g, (_, attributes, content) => {
        const sanitizedContent = content.trim().replace(/"/g, '&quot;');
        const code = `code="${sanitizedContent}"`;
        return `<code-snippet${attributes.replace(/class="(.*?)"/, (_, language) => language ? ` language="${language}"` : '')} ${DOMPurify.sanitize(code)}></code-snippet>`;
      }).replace(/<table>([\s\S]*?)<\/table>/g, '<div class="biel-table-container"><table>$1</table></div>');

    const visibleDiv = this.el.shadowRoot.querySelector('#biel-message') as HTMLElement;
    const newElements = this.convertToElement(htmlSnippet);

    if (this.isPartial) {
      this.deepCompareAndReplace(visibleDiv, newElements);
    } else {
      this.content = htmlSnippet;
    }
  }

  processTable(element: HTMLElement) {
    const tableElements = element.querySelectorAll('table');
    tableElements.forEach((tableElement) => {
      const table = document.createElement('biel-table');
      table.setAttribute('tableData', tableElement.outerHTML);
      tableElement.replaceWith(table);
    });
  }

  convertToElement(content: string): HTMLElement {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    return tempDiv;
  }

  deepCompareAndReplace(element1: HTMLElement, element2: HTMLElement): void {

    if (element2.hasAttributes()) {
      this.synchronizeAttributes(element1, element2);
    }

    if (!element1) {
      element1 = element2;
    }

    const childNodes1 = Array.from(element1.childNodes);
    const childNodes2 = Array.from(element2.childNodes);

    const maxLength = Math.max(childNodes1.length, childNodes2.length);

    for (let i = 0; i < maxLength; i++) {
      const child1 = childNodes1[i];
      const child2 = childNodes2[i];

      if (!child1 && child2) {
        element1.appendChild(child2.cloneNode(true));
      } else if (child1 && !child2) {
        element1.removeChild(child1);
      } else if (child1 && child2) {
        if (!this.areNodesEqual(child1, child2)) {
          element1.replaceChild(child2.cloneNode(true), child1);
        } else if (child1.nodeType === Node.ELEMENT_NODE) {
          this.deepCompareAndReplace(child1 as HTMLElement, child2 as HTMLElement);
        }
      }
    }
  }

  areNodesEqual(node1: Node, node2: Node): boolean {
    if (node1.nodeType !== node2.nodeType) return false;

    if (node1.nodeType === Node.TEXT_NODE) {
      return node1.textContent === node2.textContent;
    }

    if (node1.nodeType === Node.ELEMENT_NODE && node2.nodeType === Node.ELEMENT_NODE) {
      const element1 = node1 as HTMLElement;
      const element2 = node2 as HTMLElement;

      return element1.tagName === element2.tagName;
    }
    return false;
  }

  synchronizeAttributes(element1: HTMLElement, element2: HTMLElement): void {
    const attributes2 = Array.from(element2.attributes).filter(
      (attr) => attr.name !== "class"
    );

    attributes2.forEach((attr) => {
      if (element1.getAttribute(attr.name) !== attr.value) {
        element1.setAttribute(attr.name, attr.value);
      }
    });
  }

  render() {
    return (
      <div class={`biel-message biel-message--${this.sender}`}>
        <div class="biel-message__avatar">
          {this.sender === 'user' && (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-user"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
          )}
          {this.sender === 'ai' && (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bot"><path d="M12 8V4H8" /><rect width="16" height="12" x="4" y="8" rx="2" /><path d="M2 14h2" /><path d="M20 14h2" /><path d="M15 13v2" /><path d="M9 13v2" /></svg>
          )}
        </div>
        <div class="biel-message__content">
          <div id='biel-message' ></div>
          {this.sender === 'ai' && this.src && this.src.length > 0 && (
            <biel-sources sources={this.src} sourcesText={this.sourcesText}></biel-sources>
          )}
          {this.sender === 'ai' && !this.hideFeedback && (this.messageId || this.feed !== undefined) && (
            <biel-feedback
              messageId={this.messageId ? this.messageId : null}
              initialReaction={this.feed !== undefined ? this.feed : null}
              bielId={this.bielId}
            ></biel-feedback>
          )}
        </div>
      </div>
    );
  }
}